import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Search } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import 'leaflet/dist/leaflet.css';

import iconoUrl from 'leaflet/dist/images/marker-icon.png';
import iconoRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import sombraUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconUrl: iconoUrl,
  iconRetinaUrl: iconoRetinaUrl,
  shadowUrl: sombraUrl,
});

const LATACUNGA: [number, number] = [-0.9339, -78.6156];

interface Punto {
  id: number;
  nombre: string;
  identificacion: string;
  direccion: string | null;
  parroquia: string | null;
  latitud: number;
  longitud: number;
  cuentas: string[];
  activo: boolean;
}

export function PaginaMapa() {
  const [escrito, setEscrito] = useState('');
  const [buscado, setBuscado] = useState('');

  const puntos = useQuery({
    queryKey: ['mapa', buscado],
    queryFn: () => api.get<Punto[]>('/mapa/clientes', { query: { buscar: buscado } }),
    enabled: buscado.length > 0,
  });

  const encontrados = puntos.data ?? [];

  return (
    <>
      <EncabezadoDePagina
        icono={MapPin}
        color="esmeralda"
        titulo="Mapa"
        subtitulo="Dónde está el predio de un cliente"
      />

      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          setBuscado(escrito.trim());
        }}
        className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4"
      >
        <label className="flex min-w-72 flex-1 items-center gap-2 rounded-lg border border-armazon-200 px-3">
          <Search className="size-4 shrink-0 text-armazon-400" aria-hidden />
          <input
            value={escrito}
            onChange={(evento) => setEscrito(evento.target.value)}
            placeholder="Nº de cuenta, cédula/RUC o nombre del cliente"
            aria-label="Buscar cliente en el mapa"
            className="h-9 flex-1 bg-transparent text-xs outline-none"
          />
        </label>

        <button
          type="submit"
          disabled={escrito.trim().length === 0}
          className="h-9 rounded-lg bg-marca-600 px-4 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Buscar
        </button>

        {buscado && !puntos.isFetching && (
          <span className="text-[11px] uppercase text-armazon-500">
            {encontrados.length === 0
              ? 'Nadie con ese dato tiene ubicación registrada'
              : `${encontrados.length} ${encontrados.length === 1 ? 'ubicación' : 'ubicaciones'}`}
          </span>
        )}
      </form>

      {puntos.isFetching ? (
        <Cargando titulo="Buscando en el catastro" detalle="Ubicando los predios" alto="h-[32rem]" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200">
          <MapContainer
            center={LATACUNGA}
            zoom={13}
            scrollWheelZoom
            style={{ height: '32rem', width: '100%' }}
          >
            <TileLayer

              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <EncuadrarEn puntos={encontrados} />

            {encontrados.map((p) => (
              <Marker key={p.id} position={[p.latitud, p.longitud]}>
                <Popup>
                  <span className="block text-xs">
                    <Link
                      to={`/clientes/${p.id}`}
                      className="font-semibold text-marca-700 underline"
                    >
                      {p.nombre}
                    </Link>
                    <span className="mt-1 block font-mono text-[10px] text-armazon-500">
                      {p.identificacion}
                    </span>
                    {p.cuentas.length > 0 && (
                      <span className="mt-1 block font-mono text-[10px] text-armazon-500">
                        {p.cuentas.join(' · ')}
                      </span>
                    )}
                    {p.direccion && <span className="mt-1 block">{p.direccion}</span>}
                    {p.parroquia && (
                      <span className="mt-1 block text-[10px] uppercase text-armazon-400">
                        {p.parroquia}
                      </span>
                    )}
                    {!p.activo && (
                      <span className="mt-1 block text-[10px] font-semibold uppercase text-red-700">
                        Dado de baja
                      </span>
                    )}
                  </span>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </>
  );
}

function EncuadrarEn({ puntos }: { puntos: { latitud: number; longitud: number }[] }) {
  const mapa = useMap();

  useEffect(() => {
    if (puntos.length === 0) {
      return;
    }

    if (puntos.length === 1) {
      mapa.setView([puntos[0]!.latitud, puntos[0]!.longitud], 17);
      return;
    }

    mapa.fitBounds(
      L.latLngBounds(puntos.map((p) => [p.latitud, p.longitud] as [number, number])),
      { padding: [40, 40] },
    );
  }, [puntos, mapa]);

  return null;
}
